import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";
import InputContainer from "./InputContainer";

export type CheckBoxProps = {
  label?: string | React.ReactNode;
  className?: string;
  value?: boolean;
  onChange?: (nextValue: boolean) => void;
  classes?: {
    input?: string;
    label?: string;
  };
  errorMessage?: string;
} & Omit<
  React.ComponentPropsWithoutRef<"input">,
  | "type"
  | "checked"
  | "defaultChecked"
  | "value"
  | "defaultValue"
  | "onChange"
  | "className"
>;

const CheckBox = React.forwardRef<HTMLInputElement, CheckBoxProps>(
  ({ label, value, onChange, classes, id, className, ...props }, ref) => {
    const autoId = React.useId();
    const resolvedId = id ?? autoId;

    return (
      <InputContainer
        bare
        className={className}
        errorMessage={props.errorMessage}
      >
        <label
          htmlFor={resolvedId}
          className={twMerge(
            "inline-flex items-center gap-2 select-none",
            props.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          )}
        >
          <input
            {...props}
            ref={ref}
            id={resolvedId}
            type="checkbox"
            checked={value ?? false}
            onChange={(e) => onChange?.(e.target.checked)}
            className={twMerge(
              "h-4 w-4 rounded-[3px] border border-input-border bg-surface text-secondary",
              "focus:!outline-none focus:!ring-0 focus:!ring-offset-0 focus:!shadow-none",
              "focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!shadow-none",
              classes?.input,
            )}
          />
          {label ? (
            <Text level={4} className={twMerge("font-[400]", classes?.label)}>
              {label}
            </Text>
          ) : null}
        </label>
      </InputContainer>
    );
  },
);

CheckBox.displayName = "CheckBox";

export default CheckBox;
